/**
 * suggestTimes (SCHED-AI-01a/b) — the scheduling copilot.
 * For a pending ticket with coordinates, scan upcoming boards and propose
 * WHEN + WHO, with three kinds of smartness (Seth, 2026-06-04):
 *
 * 1. TECH LOCKS — a ticket's affinity tech (parts/returns), or a customer's
 *    STATED request in their location notes ("Brent Only" — the same
 *    negation-aware detector the board engine uses), restricts suggestions to
 *    that tech's days and the lock is surfaced explicitly. Excluded techs
 *    ("do not send X") are never suggested.
 * 2. URGENCY WINDOWS — ASAP scans today + the next few days and ranks
 *    earliest-first; SOON scans two weeks balanced; ROUTINE (tune-ups) starts
 *    NEXT week and deliberately fills LIGHT days…
 * 3. …with HEADROOM — routine work avoids tech-days that already carry the
 *    routine cap of maintenance-looking calls, so no-heat/no-cool demand
 *    always has somewhere to land. (SCHED-AI-02: the cap is an org_setting,
 *    editable in Settings → AI controls; default 3.)
 *
 * SCHED-AI-02 also: inferUrgency() reads the ticket's own text and guesses
 * ASAP / soon / routine WITH the matched phrase as evidence — the dispatcher
 * sees "looks urgent — 'no heat'" and can always override. A guess, labeled
 * as a guess, never silent.
 *
 * Numbers come from the board's own insertion math — derived, never invented.
 * PROPOSES ONLY: "Set as target" writes the plan; booking stays in ESC.
 */
import { supabase } from "@/lib/supabase";
import { displayCustomerName, extractJobPreview } from "@/lib/format";
import {
  deriveRequestedTech,
  deriveExcludedTechs,
} from "@/lib/recommendations";
import { PSEUDO_CUSTOMERS } from "./api";
import { usableCoords } from "@/lib/route";
import { STATUS_DONE } from "@/lib/types";
import type { PendingTicket } from "./queue";

export type Urgency = "asap" | "soon" | "routine";

export const URGENCY_LABEL: Record<Urgency, { label: string; hint: string }> = {
  asap: { label: "ASAP", hint: "today + the next few days, earliest first" },
  soon: { label: "Soon", hint: "the next two weeks, best fit" },
  routine: {
    label: "Routine",
    hint: "starts next week — fills light days, keeps room for emergencies",
  },
};

/** Sedgwick's bookable arrival windows + each one's start/end hour.
 * 8AM = the first-call slot (a promise to be there at day start). */
export const ARRIVAL_WINDOWS: { code: string; start: number; end: number }[] = [
  { code: "8AM", start: 8, end: 9 },
  { code: "8-12", start: 8, end: 12 },
  { code: "10-2", start: 10, end: 14 },
  { code: "12-4", start: 12, end: 16 },
  { code: "3-7", start: 15, end: 19 },
];

/** One existing stop on a tech's day — for the mini-board lane render. */
export interface LaneStop {
  /** Hour-of-day (may be fractional) for ordering + token placement. */
  hour: number;
  city: string | null;
  /** Arrival WINDOW (customer promise) — parsed from the note lead ("8-12, …"). */
  window: string | null;
  /** Priority TAG (TECH / LONG / PART …) — the window_code when it's a special code. */
  tag: string | null;
  /** TECH-DAY-DETAIL (34i87, Seth: "more details on the existing tech's other
   *  calls is needed — at a quick glance I can know what the other calls on
   *  the tech's schedule are"): who, what, where, how long. */
  name: string | null;
  what: string | null;
  address: string | null;
  minutes: number | null;
  status: string | null;
  /** True for a Pegasus-native booking (vs an ESC dispatch). */
  pegasus: boolean;
}

/** The arrival window a dispatcher wrote at the START of the note ("8-12, …"). */
function parseLeadWindow(notes: string | null): string | null {
  const m = (notes ?? "").trim().match(/^(8\s*-\s*12|10\s*-\s*2|12\s*-\s*4|3\s*-\s*7|8\s*am)/i);
  if (!m) return null;
  const w = m[1].toLowerCase().replace(/\s/g, "");
  return w === "8am" ? "8AM" : w;
}

export interface TimeSuggestion {
  dateISO: string;
  dayLabel: string;
  empno: string;
  techName: string;
  addedMiles: number;
  stopCount: number;
  maintCount: number; // maintenance-looking calls already on his day
  dominantCity: string | null;
  isAffinity: boolean;
  matchesTarget: boolean;
  /** The bookable arrival window this slot falls in (e.g. "12-4"). */
  window: string;
  /** Exactly where on the board it lands, in dispatcher words:
   * "after his 11:00 AM Edina stop" / "to start his day" / "to end his day". */
  placement: string;
  /** The hour the recommended slot lands at — for the mini-board token. */
  slotHour: number;
  /** The tech's day, hour-sorted, for the mini-board lane render. */
  stops: LaneStop[];
}

export interface SuggestResult {
  suggestions: TimeSuggestion[];
  /** Why suggestions are restricted to one tech (shown as a lock chip). */
  lock: { empno: string; name: string; reason: string } | null;
  excludedNames: string[];
}

interface Roster {
  empno: string;
  first: string | null;
}

function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function dayLabelOf(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

interface BoardRow {
  tech_empno: string;
  custno: string;
  loc_latitude: number | null;
  loc_longitude: number | null;
  city: string | null;
  window_code: string | null;
  notes: string | null;
  tpromtime: string | null;
  status: string | null;
  // TECH-DAY-DETAIL — optional so the capacity math's older callers still fit.
  customer_fullname?: string | null;
  locname?: string | null;
  service_address?: string | null;
  dispatch_est_min?: number | null;
  /** Pegasus-native rows carry their own type name; ESC rows derive it from the notes. */
  what?: string | null;
  pegasus?: boolean;
}

/** The lane card's facts for one stop — shared by both suggestion builders. */
function laneStopOf(s: BoardRow): LaneStop {
  const code = (s.window_code ?? "").trim().toUpperCase();
  const isWin = /^(8AM|8-12|10-2|12-4|3-7|AFTER)$/.test(code);
  // Arrival window = note lead ("8-12 …") or a window-coded priority; the
  // TAG (TECH/LONG/PART) is the window_code when it's a special code. BOTH
  // shown on the card (Seth 2026-06-23).
  const name = s.pegasus ? (s.customer_fullname ?? null) : displayCustomerName(s.locname, s.customer_fullname);
  const what = s.what ?? (s.notes ? extractJobPreview(s.notes, 90) : null);
  return {
    hour: stopHour(s),
    city: (s.city ?? "").trim() || null,
    window: parseLeadWindow(s.notes) ?? (isWin ? code : null),
    // ESC leaves "------" in the code column on placeholder rows — not a tag.
    tag: isWin ? null : /[A-Z0-9]/.test(code) ? code : null,
    name: name && name !== "Customer" ? name : null,
    what: what && what !== "—" ? what : null,
    address: (s.service_address ?? "").trim() || null,
    minutes: s.dispatch_est_min ?? null,
    status: s.status ?? null,
    pegasus: !!s.pegasus,
  };
}

/** Hour-of-day for a stop: promised time first, else its window's start. */
function stopHour(r: BoardRow): number {
  const t = /^(\d{2}):/.exec(r.tpromtime ?? "");
  if (t) return parseInt(t[1], 10);
  const w = /^(\d{1,2})/.exec((r.window_code ?? "").trim());
  if (w) {
    let h = parseInt(w[1], 10);
    if (h >= 1 && h <= 7) h += 12;
    return h;
  }
  return 12;
}

function fmtHour(r: BoardRow): string {
  const t = /^(\d{2}):(\d{2})/.exec(r.tpromtime ?? "");
  if (!t) return r.window_code ?? "";
  let h = parseInt(t[1], 10);
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${t[2]} ${ampm}`;
}

/** Best insertion position (not just cost) into an hour-ordered route. */
function bestInsertion(
  seq: { lat: number; lng: number }[],
  p: { lat: number; lng: number },
): { cost: number; index: number } {
  const dist = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
    const dy = (a.lat - b.lat) * 69.17;
    const dx =
      (a.lng - b.lng) * 69.17 * Math.cos(((a.lat + b.lat) / 2) * (Math.PI / 180));
    return Math.sqrt(dx * dx + dy * dy) * 1.3;
  };
  if (seq.length === 0) return { cost: 0, index: 0 };
  let best = { cost: dist(p, seq[0]), index: 0 }; // prepend
  const append = { cost: dist(seq[seq.length - 1], p), index: seq.length };
  if (append.cost < best.cost) best = append;
  for (let i = 0; i < seq.length - 1; i++) {
    const cost = dist(seq[i], p) + dist(p, seq[i + 1]) - dist(seq[i], seq[i + 1]);
    if (cost < best.cost) best = { cost, index: i + 1 };
  }
  return best;
}

/** The bookable window covering an hour (midpoint of the insertion slot). */
function windowForHour(h: number): string {
  if (h < 8.75) return "8AM";
  if (h < 10) return "8-12";
  if (h < 12) return "10-2";
  if (h < 15) return "12-4";
  return "3-7";
}

/** A day is "full" for new work at this many stops. */
const FULL_DAY = 9;
/** Default max maintenance-looking calls per tech-day before routine work
 * skips it — the no-heat/no-cool headroom rule. Admin-editable via the
 * 'routine_cap_per_day' org_setting (Settings → AI controls). */
export const DEFAULT_ROUTINE_CAP = 3;
const MAINT_RE = /\b(ck|check|tune[\s-]?up|maint)\b/i;

// ---------- Urgency inference (SCHED-AI-02) ----------

export interface UrgencyGuess {
  urgency: Urgency;
  /** The matched phrase — shown to the dispatcher as the WHY. */
  evidence: string;
}

/** Demand-call language: the negation IS the emergency ("no heat"). */
const ASAP_RE =
  /\b(no (heat|heating|cool|cooling|ac|a\/c|air|hot water)|not (heating|cooling|working)|stopped working|won'?t (start|run|turn on|stay (on|lit))|emergency|water leak(ing)?|leaking water|flood(ing|ed)?|gas (smell|leak)|smells? (like )?gas|carbon monoxide|co (alarm|detector)|frozen|freezing)\b/i;
/** The customer explicitly relaxed it ("no rush") — also negation-aware:
 * "not an emergency" must never read as "emergency". */
const RELAXED_RE = /\b(no rush|not? (an? )?(emergency|urgent)|whenever)\b/i;
const SOON_RE =
  /\b(part (is )?(in|arrived|here)|return (visit|trip)|follow[\s-]?up|call[\s-]?back|intermittent)\b/i;
const ROUTINE_RE = /\b(tune[\s-]?up|maint\w*|agreement|precision|ck|check)\b/i;

/**
 * Guess urgency from the ticket's own words, with the evidence attached.
 * Text signals beat lane defaults; an explicit "no rush" beats a generic
 * "emergency" word; null = no opinion (UI keeps its default).
 */
export function inferUrgency(
  text: string,
  laneKey?: string | null,
): UrgencyGuess | null {
  const t = text ?? "";
  const relaxed = RELAXED_RE.exec(t);
  const asap = ASAP_RE.exec(t);
  // "not an emergency" suppresses a bare emergency/urgent hit, but never a
  // hard symptom like "no heat".
  if (asap && !(relaxed && /emergenc|urgent/i.test(asap[0]))) {
    return { urgency: "asap", evidence: asap[0] };
  }
  if (relaxed) return { urgency: "routine", evidence: relaxed[0] };
  const soon = SOON_RE.exec(t);
  if (soon) return { urgency: "soon", evidence: soon[0] };
  const routine = ROUTINE_RE.exec(t);
  if (routine) return { urgency: "routine", evidence: routine[0] };
  if (laneKey === "campaign")
    return { urgency: "routine", evidence: "campaign batch" };
  if (laneKey === "order_parts")
    return { urgency: "soon", evidence: "part ticket" };
  if (laneKey === "return_visit")
    return { urgency: "soon", evidence: "return visit" };
  return null;
}

export interface SuggestConstraints {
  /** Customer's window constraint ("12-4") or null = any. */
  windowPref: string | null;
  /** Allowed weekdays, JS getDay() values 1–5 (Mon–Fri). Null/full = all. */
  weekdays: Set<number> | null;
  /** Explicit date range ("these dates") — overrides the urgency window;
   * urgency still drives the RANKING. */
  from: string | null;
  to: string | null;
  /** Admin-configured routine headroom cap (org_setting); default 3. */
  routineCap?: number;
  /** The dispatcher has EXPLICITLY picked a tech + day themselves — render that
   * lane even if the headroom cap / full-day guards would auto-skip it. We
   * inform, never block a manual choice (Seth 2026-06-23). */
  explicit?: boolean;
  /** Soft preference — when no tech is locked, prefer these techs (customer's
   * preferred tech first, then senior techs) per day, without hard-locking. */
  preferEmpnos?: string[];
}

/** Candidate days: explicit range when given, else the urgency window —
 * always weekday-filtered ("Tuesdays and Fridays"), capped to stay snappy. */
function buildDays(urgency: Urgency, c: SuggestConstraints): string[] {
  const allow = (d: Date) => {
    const dow = d.getDay();
    if (dow === 0 || dow === 6) return false;
    return !c.weekdays || c.weekdays.size === 0 || c.weekdays.has(dow);
  };
  const days: string[] = [];
  const today = toISO(new Date());

  if (c.from || c.to) {
    const cursor = c.from ? new Date(`${c.from}T00:00:00`) : new Date();
    if (toISO(cursor) < today) {
      // Never scan the past.
      while (toISO(cursor) < today) cursor.setDate(cursor.getDate() + 1);
    }
    const endISO = c.to ?? c.from ?? toISO(new Date(Date.now() + 28 * 86400000));
    while (toISO(cursor) <= endISO && days.length < 15) {
      if (allow(cursor)) days.push(toISO(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }

  const cursor = new Date();
  const startOffset = urgency === "routine" ? 5 : 0; // routine starts ~next week
  const count = urgency === "asap" ? 4 : urgency === "soon" ? 10 : 12;
  let skipped = 0;
  let guard = 0;
  // ASAP includes TODAY (the live board); others start tomorrow.
  if (urgency !== "asap") cursor.setDate(cursor.getDate() + 1);
  while (days.length < count && guard < 45) {
    guard++;
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) {
      if (skipped >= startOffset) {
        if (allow(cursor)) days.push(toISO(cursor));
      } else skipped++;
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

/**
 * The copilot proposes times for SERVICE and PLUMBING work only (Seth, 2026-06-09).
 * Install, Sales, and New Construction are scheduled through their own process and never
 * get auto-proposed times. Mechanism with today's data: install + sales ride the "install"
 * lane and NC is separate, so excluding those lanes leaves service + plumbing-service calls
 * (the normal service lanes) eligible. When a department/work-type lands on the pending
 * ticket directly, gate on that instead of the lane.
 */
const NON_COPILOT_LANES = new Set(["install", "new_construction", "nc"]);
export function isCopilotEligible(t: Pick<PendingTicket, "lane_key">): boolean {
  return !NON_COPILOT_LANES.has(t.lane_key);
}

export async function suggestTimes(
  t: PendingTicket,
  roster: Roster[],
  techName: (empno: string) => string,
  urgency: Urgency,
  constraints: SuggestConstraints,
): Promise<SuggestResult> {
  // Service + plumbing only — install / sales / new construction are scheduled separately.
  if (!isCopilotEligible(t))
    return { suggestions: [], lock: null, excludedNames: [] };
  if (t.lat == null || t.lng == null)
    return { suggestions: [], lock: null, excludedNames: [] };
  const windowPref = constraints.windowPref;
  const point = { lat: t.lat, lng: t.lng };
  const days = buildDays(urgency, constraints);
  if (days.length === 0)
    return { suggestions: [], lock: null, excludedNames: [] };

  // --- Tech locks: explicit affinity wins; else the customer's STATED
  // request from their location notes (negation-aware, same as the board). ---
  let lock: SuggestResult["lock"] = null;
  let excluded: string[] = [];
  if (t.custno && t.locno) {
    const { data: locRows } = await supabase
      .from("mirror_location")
      .select("notes")
      .eq("custno", t.custno)
      .eq("locno", t.locno)
      .limit(1);
    const locNotes = (locRows?.[0] as { notes: string | null } | undefined)?.notes ?? "";
    excluded = deriveExcludedTechs(locNotes, roster);
    const requested = deriveRequestedTech(locNotes, roster);
    if (t.affinity_empno) {
      lock = {
        empno: t.affinity_empno,
        name: techName(t.affinity_empno),
        reason: "his ticket",
      };
    } else if (requested && !excluded.includes(requested)) {
      lock = {
        empno: requested,
        name: techName(requested),
        reason: "requested — location notes",
      };
    }
  } else if (t.affinity_empno) {
    lock = {
      empno: t.affinity_empno,
      name: techName(t.affinity_empno),
      reason: "his ticket",
    };
  }

  // Availability across the window — full-day OUT (vacation/sick) AND TIMED blocks
  // (a meeting / training / errand at a set time). The router must respect a meeting
  // on a tech at 8am, not just a whole day off (Seth 2026-06-23, tech_availability).
  const { data: availRows } = await supabase
    .from("tech_availability")
    .select("empno, start_at, end_at, all_day")
    .neq("status", "denied")
    .lt("start_at", `${days[days.length - 1]}T23:59:59`)
    .gt("end_at", `${days[0]}T00:00:00`);
  const avail = (availRows ?? []) as { empno: string; start_at: string; end_at: string; all_day: boolean }[];
  const outOn = (empno: string, dayISO: string) =>
    avail.some(
      (a) =>
        a.all_day && a.empno === empno &&
        a.start_at < `${dayISO}T23:59:59` &&
        a.end_at > `${dayISO}T00:00:00`,
    );
  // Clock hours a TIMED block occupies, per tech per day — so a meeting at 8am
  // blocks the 8am suggestion, and a morning training blocks the morning.
  const blockedHours = new Map<string, Set<number>>();
  for (const a of avail) {
    if (a.all_day) continue;
    const cur = new Date(a.start_at);
    const end = new Date(a.end_at);
    while (cur < end) {
      const key = `${a.empno}|${toISO(cur)}`;
      const set = blockedHours.get(key) ?? new Set<number>();
      set.add(cur.getHours());
      blockedHours.set(key, set);
      cur.setHours(cur.getHours() + 1, 0, 0, 0);
    }
  }

  const boards = await Promise.all(
    days.map((day) =>
      supabase
        .from("v_route_data")
        .select(
          "tech_empno, custno, loc_latitude, loc_longitude, city, window_code, notes, tpromtime, status, customer_fullname, locname, service_address, dispatch_est_min",
        )
        .eq("appointment_date", day),
    ),
  );

  // Pegasus-native scheduled jobs occupy capacity too — the copilot must count
  // what PEGASUS has booked, not just ESC (Seth 2026-06-23). Same BoardRow shape.
  const { data: pegRows } = await supabase
    .from("pegasus_dispatch")
    .select("assigned_empno, scheduled_date, scheduled_window, lat, lng, city, summary, status, customer_name, service_type_name, address, est_install_minutes")
    .eq("status", "scheduled")
    .is("deleted_at", null)
    .in("scheduled_date", days);
  const pegByDay = new Map<string, BoardRow[]>();
  for (const p of (pegRows ?? []) as {
    assigned_empno: string | null; scheduled_date: string; scheduled_window: string | null;
    lat: number | null; lng: number | null; city: string | null; summary: string | null;
    customer_name: string | null; service_type_name: string | null; address: string | null; est_install_minutes: number | null;
  }[]) {
    if (!p.assigned_empno) continue;
    const row: BoardRow = {
      tech_empno: p.assigned_empno, custno: `pegasus-${p.scheduled_date}`,
      loc_latitude: p.lat, loc_longitude: p.lng, city: p.city,
      window_code: p.scheduled_window, notes: p.summary, tpromtime: null, status: "Pending",
      customer_fullname: p.customer_name, service_address: p.address, dispatch_est_min: p.est_install_minutes,
      what: p.service_type_name ?? p.summary, pegasus: true,
    };
    pegByDay.set(p.scheduled_date, [...(pegByDay.get(p.scheduled_date) ?? []), row]);
  }

  const out: TimeSuggestion[] = [];
  const todayISO = toISO(new Date());
  const nowHour = new Date().getHours();
  days.forEach((dayISO, i) => {
    const rawRows = [...((boards[i].data ?? []) as BoardRow[]), ...(pegByDay.get(dayISO) ?? [])];
    // ASSIGN-AVAIL-01 (Seth 2026-10-13: "techs that are actually off today
    // can run the call"): a day off entered in ESC is an "Off Today" dispatch
    // with window OFF — tech_availability never hears about it (that table
    // only holds Pegasus-entered time off; it was EMPTY the morning four
    // techs were off). Dropping OFF rows made an off tech look like a tech
    // with an enviably empty board. An OFF row = the tech is GONE that day.
    const escOff = new Set(
      rawRows.filter((r) => (r.window_code ?? "").trim().toUpperCase() === "OFF").map((r) => r.tech_empno),
    );
    const rows = rawRows.filter(
      (r) =>
        !PSEUDO_CUSTOMERS.has(r.custno) &&
        r.window_code !== "OFF" &&
        // Placement must match what the dispatcher SEES on the Active board:
        // completed stops are gone, and today's already-passed stops can't be
        // inserted before (Seth: "don't even see that on Brent's board").
        !STATUS_DONE.has(r.status ?? "") &&
        // NO coord filter here — a stop occupies the tech's TIME even with no map
        // coords (e.g. an office ticket). Coords are filtered below for the mileage
        // math only (Seth 2026-06-23: Ben offered 8AM despite an 8:00 office ticket).
        (dayISO !== todayISO || stopHour(r) >= nowHour - 1),
    );
    const byTech = new Map<string, BoardRow[]>();
    for (const r of rows) {
      byTech.set(r.tech_empno, [...(byTech.get(r.tech_empno) ?? []), r]);
    }

    const candidates: [string, BoardRow[]][] = lock
      ? [[lock.empno, byTech.get(lock.empno) ?? []]]
      : [...byTech.entries()].filter(([e]) => !excluded.includes(e));

    for (const [empno, allStops] of candidates) {
      if (outOn(empno, dayISO) || escOff.has(empno)) continue;
      const blocked = blockedHours.get(`${empno}|${dayISO}`) ?? null;
      if (allStops.length >= FULL_DAY && !constraints.explicit) continue;
      const maintCount = allStops.filter((s) =>
        MAINT_RE.test(s.notes ?? ""),
      ).length;
      // HEADROOM: routine work never lands on a day already carrying its
      // share of tune-ups — demand calls need somewhere to go. BUT a dispatcher
      // who explicitly picked this tech+day overrides the cap (we render it).
      if (
        urgency === "routine" &&
        maintCount >= (constraints.routineCap ?? DEFAULT_ROUTINE_CAP) &&
        !constraints.explicit
      )
        continue;

      // Hour-ordered day for the MILEAGE/placement math — coord stops only
      // (non-coord office tickets still count for capacity + the 8AM guards).
      const ordered = [...allStops]
        .filter((s) => usableCoords(s.loc_latitude, s.loc_longitude))
        .sort((a, b) => stopHour(a) - stopHour(b));
      const pts = ordered.map((s) => ({
        lat: s.loc_latitude as number,
        lng: s.loc_longitude as number,
      }));
      const ins = bestInsertion(pts, point);

      // What hour does this slot land at? Midpoint of its neighbors (or the
      // neighbor's hour at the day's edges; empty day = mid-morning).
      const before = ordered[ins.index - 1];
      const after = ordered[ins.index];
      const slotHour = before && after
        ? (stopHour(before) + stopHour(after)) / 2
        : before
          ? stopHour(before) + 1
          : after
            ? stopHour(after) - 1
            : 10;
      let window = windowForHour(slotHour);
      // The tech is in a meeting / training / errand at this hour → skip the slot.
      if (blocked && (blocked.has(Math.floor(slotHour)) || blocked.has(Math.ceil(slotHour)))) continue;
      // "8AM" is the FIRST-call promise — a slot behind another stop, a tech who
      // already has an at/before-8 stop, OR an 8am block, can't be it.
      if (window === "8AM" && (ins.index > 0 || allStops.some((s) => stopHour(s) <= 8) || !!blocked?.has(8))) window = "8-12";

      // Today: never offer a window that's already behind us.
      if (dayISO === todayISO && windowPref) {
        const w = ARRIVAL_WINDOWS.find((x) => x.code === windowPref);
        if (w && w.end <= nowHour) continue;
      }
      if (dayISO === todayISO && slotHour < nowHour + 1) {
        window = windowForHour(nowHour + 1);
      }

      // 8AM = the first-call promise: only the front of his day, and only if
      // nobody already holds his 8AM slot.
      if (windowPref === "8AM") {
        if (dayISO === todayISO && nowHour >= 9) continue;
        // The 8AM first-call slot is taken if anyone is already tagged 8AM OR
        // actually promised at/ before 8:00 (Seth 2026-06-23: a tech with an
        // 8:00 stop — even tagged QUOTE/TECH — must not be offered 8AM again).
        if (
          allStops.some(
            (s) => (s.window_code ?? "").trim().toUpperCase() === "8AM" || stopHour(s) <= 8,
          ) || !!blocked?.has(8)
        )
          continue;
        ins.index = 0;
        ins.cost = pts.length > 0 ? bestInsertion([pts[0]], point).cost : 0;
        window = "8AM";
      }
      // Customer has a stated window in mind → only offer slots that honor it.
      else if (windowPref) {
        const w = ARRIVAL_WINDOWS.find((x) => x.code === windowPref);
        if (w) {
          // Re-place against just the stops bordering that window so the
          // detour math reflects WHERE he'd actually be at that time.
          const windowed = ordered.filter((s) => {
            const h = stopHour(s);
            return h >= w.start - 2 && h <= w.end + 2;
          });
          if (windowed.length === 0 && ordered.length > 0) continue; // his day never touches that window
          const wpts = windowed.map((s) => ({
            lat: s.loc_latitude as number,
            lng: s.loc_longitude as number,
          }));
          const wins = bestInsertion(wpts, point);
          ins.cost = wins.cost;
          ins.index = ordered.indexOf(windowed[wins.index - 1] ?? windowed[0]);
          if (ins.index < 0) ins.index = 0;
          else ins.index += 1;
          window = windowPref;
        }
      }

      const placeBefore = ordered[ins.index - 1];
      const placeAfter = ordered[ins.index];
      const placement = placeBefore
        ? `after his ${fmtHour(placeBefore)}${placeBefore.city ? ` ${placeBefore.city}` : ""} stop`
        : placeAfter
          ? `to start his day, before ${fmtHour(placeAfter)}${placeAfter.city ? ` in ${placeAfter.city}` : ""}`
          : "an open day";

      const cityCounts = new Map<string, number>();
      for (const s of ordered) {
        const c = (s.city ?? "").trim();
        if (c) cityCounts.set(c, (cityCounts.get(c) ?? 0) + 1);
      }
      // The tech's whole day (incl. non-coord office tickets), hour-sorted, so the
      // mini-board lane shows the real day + where the new job would land.
      const laneStops: LaneStop[] = [...allStops].sort((a, b) => stopHour(a) - stopHour(b)).map(laneStopOf);
      out.push({
        dateISO: dayISO,
        dayLabel: dayLabelOf(dayISO),
        empno,
        techName: techName(empno),
        addedMiles: ins.cost,
        stopCount: ordered.length,
        maintCount,
        dominantCity:
          [...cityCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null,
        isAffinity: !!lock && empno === lock.empno,
        matchesTarget: t.target_date === dayISO,
        window,
        placement,
        slotHour,
        stops: laneStops,
      });
    }
  });

  // --- Ranking by urgency ---
  if (urgency === "asap") {
    // Time beats miles: earliest day first, then least detour.
    out.sort(
      (a, b) => a.dateISO.localeCompare(b.dateISO) || a.addedMiles - b.addedMiles,
    );
  } else if (urgency === "routine") {
    // Fill the LIGHTEST days with the best route fit.
    out.sort(
      (a, b) =>
        (b.matchesTarget ? 1 : 0) - (a.matchesTarget ? 1 : 0) ||
        a.stopCount + a.maintCount - (b.stopCount + b.maintCount) ||
        a.addedMiles - b.addedMiles,
    );
  } else {
    // Soon: best fit, but SOONER still matters — an empty day a month out
    // must not beat a decent slot this week.
    const dayRank = new Map(days.map((d, i) => [d, i]));
    const score = (s: TimeSuggestion) =>
      s.addedMiles + 0.6 * s.stopCount + 1.2 * (dayRank.get(s.dateISO) ?? 0);
    out.sort(
      (a, b) =>
        (b.matchesTarget ? 1 : 0) - (a.matchesTarget ? 1 : 0) ||
        score(a) - score(b),
    );
  }

  // One suggestion per DAY (the route-best, since `out` is urgency-sorted), but
  // PREFER the customer's preferred/senior techs for a day when they're available —
  // so "wants Brent or a senior" surfaces them across dates, not just the
  // route-best tech, while days they're off still show the best alternative
  // (Seth 2026-06-23). Day ORDER stays urgency-ranked.
  const pref = constraints.preferEmpnos ?? [];
  const prefRank = (e: string) => { const i = pref.indexOf(e); return i < 0 ? 999 : i; };
  const dayPick = new Map<string, TimeSuggestion>();
  const dayOrder: string[] = [];
  for (const s of out) {
    const cur = dayPick.get(s.dateISO);
    if (!cur) { dayPick.set(s.dateISO, s); dayOrder.push(s.dateISO); continue; }
    if (prefRank(s.empno) < prefRank(cur.empno)) dayPick.set(s.dateISO, s);
  }
  const top = dayOrder.slice(0, 4).map((d) => dayPick.get(d)!);
  return {
    suggestions: top,
    lock,
    excludedNames: excluded.map((e) => techName(e)),
  };
}

// ============================================================================
// SCHED-FLOW-01 (Seth 2026-09-17: "rethink how best a dispatcher trying to
// figure out what days might work with the customer and how the dispatcher
// can easily swap around") — the AVAILABILITY GRID. Same board math as
// suggestTimes, but instead of returning four picks it scores EVERY
// (day × arrival window) for every tech on the roster, so the dispatcher sees
// the whole two weeks at once and clicks the cell the customer agrees to.
// ============================================================================

export interface GridOptions {
  /** Calendar days to score (ISO), already filtered to the days the office works. */
  days: string[];
  /** Arrival windows to score — the service set, or the install set. */
  windows: string[];
  urgency: Urgency;
  routineCap?: number;
  /** Customer's preferred / senior techs — shown first in a cell when they fit. */
  preferEmpnos?: string[];
  /** The dispatcher picked ONE tech — only his lanes are scored (and the
   *  full-day / headroom guards are informational, never blocking). */
  onlyEmpno?: string | null;
  /** The dispatcher picked SEVERAL techs ("Wayne or Miles is fine") — only
   *  their lanes are scored, same rules as onlyEmpno. Wins over onlyEmpno. */
  onlyEmpnos?: string[] | null;
  /** SKILL-STEER-01: the techs QUALIFIED for this call (level + specialties,
   *  Settings → Skills & Levels). Only their lanes are scored, under the
   *  normal full-day / headroom guards — a filter, not a pick. Null or empty
   *  = everyone. The dispatcher's own pick (onlyEmpno/s) wins over it. */
  allowEmpnos?: string[] | null;
}

export interface GridResult {
  /** `${dateISO}|${window}` → options, best first (one per tech). */
  cells: Map<string, TimeSuggestion[]>;
  lock: SuggestResult["lock"];
  excludedNames: string[];
  /** Days where at least one tech had ANY capacity — the rest render "full". */
  openDays: Set<string>;
}

export const cellKey = (dateISO: string, window: string) => `${dateISO}|${window}`;

/** Start/end hours of any window the office uses — the service set has fixed
 *  spans; an install or custom window ("7-8", "11-1", "7:30-9") is parsed. */
export function windowSpan(code: string): { start: number; end: number } | null {
  const known = ARRIVAL_WINDOWS.find((w) => w.code === code);
  if (known) return { start: known.start, end: known.end };
  const m = /^(\d{1,2})(?::(\d{2}))?\s*-\s*(\d{1,2})(?::(\d{2}))?$/.exec(code.trim());
  if (!m) return null;
  let s = parseInt(m[1], 10) + (m[2] ? parseInt(m[2], 10) / 60 : 0);
  let e = parseInt(m[3], 10) + (m[4] ? parseInt(m[4], 10) / 60 : 0);
  if (s >= 1 && s <= 6) s += 12;
  if (e >= 1 && e <= 7 && e < s) e += 12;
  if (e <= s) e = s + 2;
  return { start: s, end: e };
}

/** A whole-day/whole-window stop count that reads as "no room" for a tech's window. */
const WINDOW_FULL = 3;

export async function availabilityGrid(
  t: PendingTicket,
  roster: Roster[],
  techName: (empno: string) => string,
  opts: GridOptions,
): Promise<GridResult> {
  const empty: GridResult = { cells: new Map(), lock: null, excludedNames: [], openDays: new Set() };
  if (t.lat == null || t.lng == null) return empty;
  const days = opts.days.filter((d) => d >= toISO(new Date()));
  if (days.length === 0 || opts.windows.length === 0) return empty;
  const point = { lat: t.lat, lng: t.lng };

  // Locks and exclusions exactly as the copilot derives them.
  let lock: SuggestResult["lock"] = null;
  let excluded: string[] = [];
  if (t.custno && t.locno) {
    const { data: locRows } = await supabase
      .from("mirror_location").select("notes").eq("custno", t.custno).eq("locno", t.locno).limit(1);
    const locNotes = (locRows?.[0] as { notes: string | null } | undefined)?.notes ?? "";
    excluded = deriveExcludedTechs(locNotes, roster);
    const requested = deriveRequestedTech(locNotes, roster);
    if (t.affinity_empno) lock = { empno: t.affinity_empno, name: techName(t.affinity_empno), reason: "his ticket" };
    else if (requested && !excluded.includes(requested)) lock = { empno: requested, name: techName(requested), reason: "requested — location notes" };
  } else if (t.affinity_empno) {
    lock = { empno: t.affinity_empno, name: techName(t.affinity_empno), reason: "his ticket" };
  }
  // The grid never hard-locks: a requested tech is a preference the cell honors
  // first, not a wall — the dispatcher sees the other techs' room too.
  const preferEmpnos = [...(lock ? [lock.empno] : []), ...(opts.preferEmpnos ?? [])];

  const { data: availRows } = await supabase
    .from("tech_availability")
    .select("empno, start_at, end_at, all_day")
    .neq("status", "denied")
    .lt("start_at", `${days[days.length - 1]}T23:59:59`)
    .gt("end_at", `${days[0]}T00:00:00`);
  const avail = (availRows ?? []) as { empno: string; start_at: string; end_at: string; all_day: boolean }[];
  const outOn = (empno: string, dayISO: string) =>
    avail.some((a) => a.all_day && a.empno === empno && a.start_at < `${dayISO}T23:59:59` && a.end_at > `${dayISO}T00:00:00`);
  const blockedHours = new Map<string, Set<number>>();
  for (const a of avail) {
    if (a.all_day) continue;
    const cur = new Date(a.start_at);
    const end = new Date(a.end_at);
    while (cur < end) {
      const key = `${a.empno}|${toISO(cur)}`;
      const set = blockedHours.get(key) ?? new Set<number>();
      set.add(cur.getHours());
      blockedHours.set(key, set);
      cur.setHours(cur.getHours() + 1, 0, 0, 0);
    }
  }

  const boards = await Promise.all(
    days.map((day) =>
      supabase
        .from("v_route_data")
        .select("tech_empno, custno, loc_latitude, loc_longitude, city, window_code, notes, tpromtime, status, customer_fullname, locname, service_address, dispatch_est_min")
        .eq("appointment_date", day),
    ),
  );
  const { data: pegRows } = await supabase
    .from("pegasus_dispatch")
    .select("assigned_empno, scheduled_date, scheduled_window, lat, lng, city, summary, status, customer_name, service_type_name, address, est_install_minutes")
    .eq("status", "scheduled")
    .is("deleted_at", null)
    .in("scheduled_date", days);
  const pegByDay = new Map<string, BoardRow[]>();
  for (const p of (pegRows ?? []) as {
    assigned_empno: string | null; scheduled_date: string; scheduled_window: string | null;
    lat: number | null; lng: number | null; city: string | null; summary: string | null;
    customer_name: string | null; service_type_name: string | null; address: string | null; est_install_minutes: number | null;
  }[]) {
    if (!p.assigned_empno) continue;
    const row: BoardRow = {
      tech_empno: p.assigned_empno, custno: `pegasus-${p.scheduled_date}`,
      loc_latitude: p.lat, loc_longitude: p.lng, city: p.city,
      window_code: p.scheduled_window, notes: p.summary, tpromtime: null, status: "Pending",
      customer_fullname: p.customer_name, service_address: p.address, dispatch_est_min: p.est_install_minutes,
      what: p.service_type_name ?? p.summary, pegasus: true,
    };
    pegByDay.set(p.scheduled_date, [...(pegByDay.get(p.scheduled_date) ?? []), row]);
  }

  const dist = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
    const dy = (a.lat - b.lat) * 69.17;
    const dx = (a.lng - b.lng) * 69.17 * Math.cos(((a.lat + b.lat) / 2) * (Math.PI / 180));
    return Math.sqrt(dx * dx + dy * dy) * 1.3;
  };
  const prefRank = (e: string) => { const i = preferEmpnos.indexOf(e); return i < 0 ? 999 : i; };
  const todayISO = toISO(new Date());
  const nowHour = new Date().getHours() + new Date().getMinutes() / 60;
  const cells = new Map<string, TimeSuggestion[]>();
  const openDays = new Set<string>();

  days.forEach((dayISO, i) => {
    const rawRows = [...((boards[i].data ?? []) as BoardRow[]), ...(pegByDay.get(dayISO) ?? [])];
    // ASSIGN-AVAIL-01: ESC-entered time off arrives as OFF dispatches, not
    // tech_availability rows — an OFF day means the tech is OUT, not open
    // (see the twin comment in suggestTimes above).
    const escOff = new Set(
      rawRows.filter((r) => (r.window_code ?? "").trim().toUpperCase() === "OFF").map((r) => r.tech_empno),
    );
    const rows = rawRows.filter(
      (r) =>
        !PSEUDO_CUSTOMERS.has(r.custno) &&
        r.window_code !== "OFF" &&
        !STATUS_DONE.has(r.status ?? "") &&
        (dayISO !== todayISO || stopHour(r) >= nowHour - 1),
    );
    const byTech = new Map<string, BoardRow[]>();
    for (const r of rows) byTech.set(r.tech_empno, [...(byTech.get(r.tech_empno) ?? []), r]);
    // Every tech on the roster is a candidate — an empty day is the best day.
    const only = opts.onlyEmpnos && opts.onlyEmpnos.length > 0 ? opts.onlyEmpnos : opts.onlyEmpno ? [opts.onlyEmpno] : null;
    const allow = !only && opts.allowEmpnos && opts.allowEmpnos.length > 0 ? opts.allowEmpnos : null;
    const candidates: [string, BoardRow[]][] = (only ? roster.filter((r) => only.includes(r.empno)) : allow ? roster.filter((r) => allow.includes(r.empno)) : roster)
      .filter((r) => only || !excluded.includes(r.empno))
      .map((r) => [r.empno, byTech.get(r.empno) ?? []]);

    for (const [empno, allStops] of candidates) {
      // Off is off — even an explicit tech pick doesn't resurrect a day off.
      if (outOn(empno, dayISO) || escOff.has(empno)) continue;
      const explicit = !!only;
      if (allStops.length >= FULL_DAY && !explicit) continue;
      const maintCount = allStops.filter((s) => MAINT_RE.test(s.notes ?? "")).length;
      if (opts.urgency === "routine" && maintCount >= (opts.routineCap ?? DEFAULT_ROUTINE_CAP) && !explicit) continue;
      const blocked = blockedHours.get(`${empno}|${dayISO}`) ?? null;
      const ordered = [...allStops]
        .filter((s) => usableCoords(s.loc_latitude, s.loc_longitude))
        .sort((a, b) => stopHour(a) - stopHour(b));
      const pts = ordered.map((s) => ({ lat: s.loc_latitude as number, lng: s.loc_longitude as number }));
      const cityCounts = new Map<string, number>();
      for (const s of ordered) { const c = (s.city ?? "").trim(); if (c) cityCounts.set(c, (cityCounts.get(c) ?? 0) + 1); }
      const dominantCity = [...cityCounts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
      const laneStops: LaneStop[] = [...allStops].sort((a, b) => stopHour(a) - stopHour(b)).map(laneStopOf);
      openDays.add(dayISO);

      for (const win of opts.windows) {
        const span = windowSpan(win);
        if (!span) continue;
        // Today: a window already behind us is gone.
        if (dayISO === todayISO && span.end <= nowHour + 0.5) continue;
        // A meeting / training over the whole window blocks it.
        if (blocked) {
          let covered = 0;
          for (let h = Math.floor(span.start); h < Math.ceil(span.end); h++) if (blocked.has(h)) covered++;
          if (covered >= Math.max(1, Math.ceil(span.end) - Math.floor(span.start))) continue;
        }
        const inWindow = allStops.filter((s) => { const h = stopHour(s); return h >= span.start && h < span.end; });
        if (inWindow.length >= WINDOW_FULL && !explicit) continue;
        let cost: number;
        let index: number;
        let slotHour: number;
        if (win === "8AM") {
          // The first-call promise: only if nobody holds his 8 o'clock.
          if (allStops.some((s) => (s.window_code ?? "").trim().toUpperCase() === "8AM" || stopHour(s) <= 8) || !!blocked?.has(8)) continue;
          // A "we're there at day start" promise can't be made once it IS
          // 8:00 (ASSIGN-AVAIL-01 — the grid offered 8AM at 8:20 that morning).
          if (dayISO === todayISO && nowHour >= 8) continue;
          cost = pts.length > 0 ? dist(point, pts[0]) : 0;
          index = 0;
          slotHour = 8;
        } else {
          const near = ordered.filter((s) => { const h = stopHour(s); return h >= span.start - 2 && h <= span.end + 2; });
          if (near.length > 0) {
            const npts = near.map((s) => ({ lat: s.loc_latitude as number, lng: s.loc_longitude as number }));
            const ins = bestInsertion(npts, point);
            cost = ins.cost;
            const anchor = near[ins.index - 1] ?? null;
            index = anchor ? ordered.indexOf(anchor) + 1 : ordered.indexOf(near[0]);
            if (index < 0) index = 0;
            const before = ordered[index - 1];
            const after = ordered[index];
            const mid = before && after ? (stopHour(before) + stopHour(after)) / 2 : before ? stopHour(before) + 1 : after ? stopHour(after) - 1 : span.start + 1;
            slotHour = Math.min(Math.max(mid, span.start), span.end - 0.5);
          } else if (ordered.length > 0) {
            // His day never touches this window — he drives from his nearest stop.
            const before = [...ordered].reverse().find((s) => stopHour(s) < span.start) ?? null;
            const after = ordered.find((s) => stopHour(s) >= span.end) ?? null;
            const anchor = before ?? after!;
            cost = dist(point, { lat: anchor.loc_latitude as number, lng: anchor.loc_longitude as number });
            index = before ? ordered.indexOf(before) + 1 : 0;
            slotHour = span.start + 1;
          } else {
            cost = 0;
            index = 0;
            slotHour = span.start + 1;
          }
          if (dayISO === todayISO && slotHour < nowHour + 1) slotHour = Math.min(nowHour + 1, span.end - 0.5);
        }
        const placeBefore = ordered[index - 1];
        const placeAfter = ordered[index];
        const placement = placeBefore
          ? `after his ${fmtHour(placeBefore)}${placeBefore.city ? ` ${placeBefore.city}` : ""} stop`
          : placeAfter
            ? `to start his day, before ${fmtHour(placeAfter)}${placeAfter.city ? ` in ${placeAfter.city}` : ""}`
            : "an open day";
        const opt: TimeSuggestion = {
          dateISO: dayISO, dayLabel: dayLabelOf(dayISO), empno, techName: techName(empno),
          addedMiles: cost, stopCount: ordered.length, maintCount, dominantCity,
          isAffinity: !!lock && empno === lock.empno, matchesTarget: t.target_date === dayISO,
          window: win, placement, slotHour, stops: laneStops,
        };
        const k = cellKey(dayISO, win);
        cells.set(k, [...(cells.get(k) ?? []), opt]);
      }
    }
  });

  // Best first in every cell: the shortest detour, then the lighter day — and
  // the customer's own tech jumps to the front when he is within a few miles
  // of the best (a requested tech is worth a short detour, not a cross-town one).
  const PREF_SLACK_MILES = 8;
  for (const [k, list] of cells) {
    list.sort((a, b) => a.addedMiles - b.addedMiles || a.stopCount - b.stopCount);
    const best = list[0];
    const pi = list.findIndex((o) => prefRank(o.empno) < 999);
    if (best && pi > 0 && list[pi].addedMiles <= best.addedMiles + PREF_SLACK_MILES) {
      const [p] = list.splice(pi, 1);
      list.unshift(p);
    }
    cells.set(k, list);
  }
  return { cells, lock, excludedNames: excluded.map((e) => techName(e)), openDays };
}
